import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Popup from "@/components/ui/popup";
import useTranslation from "next-translate/useTranslation";
import Script from "next/script";
import { useRouter } from "next/router";
import { generateId } from "@/util/generateId";
import FBScript from "@/components/util/fb-script";
import AppleButton from "@/components/ui/apple-button";
import FacebookButton from "@/components/ui/facebook-button";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import styles from "@/modules/register/styles/Register.module.css";
import {
  signInWithCredentials,
  signInFBButton,
  signInAppleButton,
  initializeGoogleButtonForSignIn,
  redirectToRegisterForNewUser,
} from "./signInActions";

export default function SignInPopup({
  onClose,
  redirectAfterDone,
  hideSignUp,
}: {
  onClose: () => void;
  redirectAfterDone?: () => void;
  hideSignUp?: boolean;
}) {
  const { t } = useTranslation("common");
  const { t: registerT } = useTranslation("auth");
  const router = useRouter();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const premium =
    typeof router.query?.premium === "string" ? router.query.premium : undefined;

  const handleSuccess = useCallback(
    (userData: any) => {
      if (redirectAfterDone) {
        redirectAfterDone();
        onClose();
      } else {
        if (userData?.premiumUpgrade) {
          router.push("/premium/welcome");
        }
        onClose();
      }
    },
    [redirectAfterDone, onClose, router]
  );

  const handleUserDoesNotExist = useCallback(
    (params: {
      accountType: "google" | "facebook" | "apple";
      credential?: string;
      token?: string;
    }) => {
      redirectToRegisterForNewUser(params, onClose);
    },
    [onClose]
  );

  const handleError = useCallback(() => {
    if (error) {
      setError(false);
      setTimeout(() => setError(true), 300);
    } else {
      setError(true);
    }
  }, [error]);

  const callbacks = useCallback(
    () => ({
      onSuccess: handleSuccess,
      onError: handleError,
      onUserDoesNotExist: handleUserDoesNotExist,
    }),
    [handleSuccess, handleError, handleUserDoesNotExist]
  );

  function sendSignIn() {
    if (!username || !password) return;
    signInWithCredentials(
      username,
      password,
      router.locale || "en",
      callbacks(),
      premium
    );
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      sendSignIn();
    }
  }

  function handleSignInFB() {
    signInFBButton(callbacks());
  }

  async function handleSignInApple() {
    await signInAppleButton(callbacks());
  }

  useEffect(() => {
    if ((window as any).google?.accounts?.id) {
      initializeGoogleButtonForSignIn(
        router.locale || "en",
        callbacks(),
        premium
      );
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).google?.accounts?.id) {
          clearInterval(checkInterval);
          initializeGoogleButtonForSignIn(
            router.locale || "en",
            callbacks(),
            premium
          );
        }
      }, 100);
      setTimeout(() => clearInterval(checkInterval), 5000);
    }
  }, [router.locale, router.query.premium]);

  return (
    <>
      <Popup
        title={t("Log in")}
        onClose={onClose}
        maxWidth="464px"
      >
        <FBScript />
        <Script
          onLoad={() => {
            (window as any).AppleID?.auth?.init?.({
              clientId: "com.chesshotel.service",
              scope: "",
              redirectURI:
                "https://www.chesshotel.com/api/v1/auth/apple/redirect",
              state: "Eksätravägen975031",
              nonce: generateId(),
              usePopup: true,
            });
          }}
          src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        />
        <Script
          src={`https://accounts.google.com/gsi/client?hl=${router.locale}`}
          strategy="afterInteractive"
          onLoad={() => {
            if ((window as any).google?.accounts?.id) {
              initializeGoogleButtonForSignIn(
                router.locale || "en",
                callbacks(),
                premium
              );
            }
          }}
        />
        <div
          className={"flex flex-col justify-center items-stretch " + styles.body}
        >
          <div
            id="signin-popup-gbtn"
            className="w-full h-[40px] max-w-[400px] mx-auto"
          />
          <div className="my-1.5" />
          <div className="flex justify-center items-center md:block">
            <FacebookButton
              onClick={handleSignInFB}
              text={t("Log in with Facebook")}
            />
          </div>
          <div
            className={`${styles.appleBtn} flex items-center justify-center md:block`}
          >
            <AppleButton
              onClick={handleSignInApple}
              text={t("Log in with Apple")}
            />
          </div>
          <div className={styles.line}>
            <span>{registerT("or")}</span>
          </div>

          {error && (
            <Alert>{registerT("Wrong username or password")}</Alert>
          )}
          <label htmlFor="signin-popup-username" className={styles.label}>
            {registerT("Username or email")}
          </label>
          <input
            id="signin-popup-username"
            autoFocus
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />

          <label htmlFor="signin-popup-password" className={styles.label}>
            {t("Password")}
          </label>
          <input
            id="signin-popup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <div className="flex justify-end mt-2 space-x-4 text-sm">
            <Link
              className="hover:text-orange-400"
              href="/signIn/passwordRecovery"
            >
              {registerT("Lost your password?")}
            </Link>
          </div>

          <div className="flex justify-center items-center md:block">
            <Button
              size="lg"
              className="py-7 mt-4 w-full text-lg font-bold"
              onClick={sendSignIn}
            >
              {t("Log in")}
            </Button>
          </div>

          {!hideSignUp && (
            <div className="flex justify-center items-center mt-4">
              <Link
                className="text-sm underline hover:text-orange-400"
                href="/register"
              >
                {t("Sign up")}
              </Link>
            </div>
          )}
        </div>
      </Popup>
    </>
  );
}
